import React, { useState, useRef, useCallback } from "react";
import IshaChat from "./IshaChat";

/**
 * Generic cancer analysis page component (for non-lung cancers).
 * Mirrors the MedX_React CancerPage design with drag-and-drop upload.
 * Uses the Lung Cancer /predict endpoint for all cancer types.
 */
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const PREDICT_URL = `${API_BASE}/predict` || "http://127.0.0.1:8000/predict";

const ACCEPTED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".heic"];

export default function CancerPage({
  cancerName,
  scanType,
  analysisText,
  stepsTitle,
  stepsText = [],
  defaultImage = "/img/lung/lung+cancer+spot.png",
}) {
  const [isLoading, setIsLoading]         = useState(false);
  const [errorMsg, setErrorMsg]           = useState("");
  const [showResults, setShowResults]     = useState(false);
  const [showMetrics, setShowMetrics]     = useState(false);
  const [apiResult, setApiResult]         = useState(null);
  const [uploadedPreview, setUploadedPreview] = useState(null);
  const [selectedFile, setSelectedFile]   = useState(null);
  const [dragOver, setDragOver]           = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (f) => {
    const lower = f.name.toLowerCase();
    const ok = ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
    if (!ok) return "Unsupported file type. Please upload a .jpg, .png, or .heic image.";
    if (f.size > 50 * 1024 * 1024) return "File exceeds 50 MB limit.";
    return null;
  };

  const handleFileSelect = (f) => {
    const err = validateFile(f);
    if (err) { setErrorMsg(err); setSelectedFile(null); return; }
    setSelectedFile(f);
    setErrorMsg("");
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelect(dropped);
  }, []);

  const onDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  const handleAnalyze = async () => {
    if (!selectedFile) { setErrorMsg("Please select an image file first"); return; }
    setIsLoading(true);
    setErrorMsg("");
    setApiResult(null);

    const reader = new FileReader();
    reader.onload = (e) =>
      setUploadedPreview({ src: e.target.result, name: selectedFile.name, size: (selectedFile.size / 1024).toFixed(1) });
    reader.readAsDataURL(selectedFile);

    const formData = new FormData();
    formData.append("image", selectedFile);

    try {
      const response = await fetch(PREDICT_URL, { method: "POST", body: formData });
      const result = await response.json();
      setIsLoading(false);
      if (response.ok) {
        setApiResult(result);
        setShowResults(true);
      } else {
        setErrorMsg("API Error: " + (result.error || "Unknown error occurred"));
        setShowResults(true);
      }
    } catch (err) {
      setIsLoading(false);
      setErrorMsg("Connection Error: " + err.message + ". Make sure the API server is running.");
      setShowResults(true);
    }
  };

  const confidencePercent = apiResult ? (apiResult.confidence * 100).toFixed(1) : null;

  return (
    <>
      <h1 className="h3 mb-3">
        <strong>{cancerName}</strong> Dashboard
      </h1>

      <div className="row">
        <p className="text-left">
          Upload {scanType} in image format and get analysis. We use a combination of Generative AI
          besides Machine Learning &amp; Deep Learning to give deeper analysis.
          <br /><br />
          <b>Steps To Use Med-X:</b><br />
          1. Drag &amp; drop your {scanType} into the upload box, or click to browse. We accept .jpg, .png, .heic files.<br />
          2. Click on Get Analysis button.<br />
          3. Hold on for a few seconds, our AI system will analyze the scan &amp; show the results for you.<br />
          4. See Metrics to see how confident the Model is about the prediction.<br />
          5. Ask Isha; she can help you with more information.
          <br /><br />
          <b>Privacy Notes:</b>
          <ul style={{ listStyleType: "none" }}>
            <li>We don&apos;t store your data without your permission.</li>
            <li>We only give predictions through our AI-based Models; once our models analyze the scan, we delete it across our servers.</li>
            <li>Always provide feedback after getting analysis.</li>
          </ul>
        </p>

        <div className="col-xl-12 col-xxl-12 d-flex">
          <div className="w-100" style={{ maxWidth: 640, margin: "0 auto" }}>
            <div className="card">
              <p className="card-title">Upload {scanType}</p>
              <form onSubmit={(e) => e.preventDefault()}>

                {/* Drag-and-drop zone */}
                <div
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onClick={() => !isLoading && fileInputRef.current?.click()}
                  style={{
                    border:       `2px dashed ${dragOver ? "var(--accent-blue)" : selectedFile ? "var(--accent-green)" : "var(--border)"}`,
                    borderRadius: "var(--radius-md)",
                    padding:      "2.5rem 1.5rem",
                    textAlign:    "center",
                    cursor:       isLoading ? "default" : "pointer",
                    transition:   "border-color 0.2s, background 0.2s",
                    background:   dragOver
                      ? "rgba(59,130,246,0.05)"
                      : selectedFile
                      ? "rgba(16,185,129,0.05)"
                      : "transparent",
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.heic"
                    style={{ display: "none" }}
                    onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                  />

                  <div style={{ color: selectedFile ? "var(--accent-green)" : "var(--text-muted)", marginBottom: 12 }}>
                    {selectedFile ? (
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14,2 14,8 20,8"/>
                        <polyline points="9,15 11,17 15,13"/>
                      </svg>
                    ) : (
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6c757d" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14,2 14,8 20,8"/>
                        <line x1="12" y1="18" x2="12" y2="12"/>
                        <line x1="9" y1="15" x2="15" y2="15"/>
                      </svg>
                    )}
                  </div>

                  {selectedFile ? (
                    <>
                      <p style={{ fontWeight: 600, fontSize: "0.95rem", marginBottom: 4 }}>{selectedFile.name}</p>
                      <p style={{ color: "var(--text-secondary)", fontSize: "0.82rem" }}>
                        {(selectedFile.size / 1024).toFixed(1)} KB &middot;{" "}
                        <span style={{ color: "var(--accent-green)" }}>Ready to analyze</span>
                      </p>
                    </>
                  ) : (
                    <>
                      <p style={{ fontWeight: 600, marginBottom: 6 }}>
                        Drag &amp; drop your scan here
                      </p>
                      <p style={{ color: "var(--text-secondary)", fontSize: "0.82rem" }}>
                        or click to browse &middot; .jpg, .png, .heic &middot; Max 50 MB
                      </p>
                    </>
                  )}
                </div>

                {isLoading && (
                  <div style={{ marginTop: "1.25rem", textAlign: "center" }}>
                    <div className="loading-spinner"></div>
                    <p>Analyzing image with AI...</p>
                  </div>
                )}

                {errorMsg && (
                  <div style={{
                    marginTop:    "1rem",
                    padding:      "10px 14px",
                    background:   "rgba(239,68,68,0.1)",
                    border:       "1px solid rgba(239,68,68,0.3)",
                    borderRadius: "var(--radius-sm)",
                    color:        "var(--accent-red)",
                    fontSize:     "0.84rem",
                  }}>
                    {errorMsg}
                  </div>
                )}

                <hr className="divider" />

                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    className="btn btn-primary"
                    style={{ flex: 1 }}
                    type="button"
                    onClick={handleAnalyze}
                    disabled={!selectedFile || isLoading}
                  >
                    {isLoading ? "Analyzing..." : showResults ? "Analyze Again" : "Get Analysis"}
                  </button>
                  {selectedFile && !isLoading && (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => { setSelectedFile(null); setErrorMsg(""); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Results */}
                {showResults && (
                  <div>
                    {apiResult ? (
                      <div className="api-result">
                        <h4>AI Analysis Results:</h4>
                        <div style={{
                          backgroundColor: apiResult.cancer_spots_detected ? "#fdf2f2" : "#f0fff4",
                          padding: 15, borderRadius: 5, margin: "10px 0",
                          border: apiResult.cancer_spots_detected ? "1px solid #f5c6cb" : "1px solid #c3e6cb",
                        }}>
                          <h3 style={{ color: apiResult.cancer_spots_detected ? "#c0392b" : "#1e6b3a" }}>
                            Predicted Diagnosis: {apiResult.predicted_class}
                          </h3>
                        </div>

                        <div className="confidence-bar">
                          <div className="confidence-fill" style={{ width: `${confidencePercent}%` }}></div>
                        </div>
                        <p style={{ textAlign: "center", margin: "5px 0" }}>
                          <small>Confidence Level: {confidencePercent}%</small>
                        </p>

                        {apiResult.marked_image && (
                          <div style={{
                            textAlign: "center", margin: "20px 0", padding: 15,
                            backgroundColor: "#fff5f5", borderRadius: 8,
                            border: apiResult.cancer_spots_detected ? "1px solid #f5c6cb" : "1px solid #d4edda",
                          }}>
                            <h4>🎯 AI Segmentation Result:</h4>
                            <img
                              src={apiResult.marked_image}
                              style={{
                                maxWidth: 420, maxHeight: 420,
                                border: apiResult.cancer_spots_detected ? "3px solid #dc3545" : "3px solid #28a745",
                                borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                              }}
                              alt="AI segmentation"
                            />
                          </div>
                        )}

                        {uploadedPreview && (
                          <div style={{ textAlign: "center", margin: "20px 0", padding: 15, backgroundColor: "#f8f9fa", borderRadius: 5 }}>
                            <h4>📋 Original Uploaded Image:</h4>
                            <img
                              src={uploadedPreview.src}
                              style={{ maxWidth: 300, maxHeight: 300, border: "2px solid #dee2e6", borderRadius: 5 }}
                              alt="Uploaded medical image"
                            />
                            <p style={{ color: "#6c757d", fontSize: 14 }}>
                              File: {uploadedPreview.name} ({uploadedPreview.size} KB)
                            </p>
                          </div>
                        )}

                        {apiResult.all_predictions && (
                          <div>
                            <h4>Detailed Analysis:</h4>
                            <div style={{ backgroundColor: "#f8f9fa", padding: 15, borderRadius: 5 }}>
                              {Object.entries(apiResult.all_predictions)
                                .sort((a, b) => b[1] - a[1])
                                .map(([cls, conf]) => (
                                  <div key={cls} style={{ margin: "8px 0" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                      <span>{cls}</span>
                                      <span>{(conf * 100).toFixed(1)}%</span>
                                    </div>
                                    <div className="confidence-bar">
                                      <div className="confidence-fill" style={{ width: `${(conf * 100).toFixed(1)}%` }}></div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <p>Models predicted it as a Positive Score for {cancerName} with 95% accuracy.</p>
                        <div className="row">
                          <div className="container">
                            <img src={defaultImage} className="img-responsive" alt="Cancer spot" />
                          </div>
                          <div className="container text-align-left">
                            <br /><br />
                            <p><strong>Med-X&apos;s Analysis:</strong></p>
                            <p>{analysisText}</p>
                          </div>
                        </div>
                        <br /><br />
                        <div className="text-align-left">
                          <p><strong>{stepsTitle}</strong></p>
                          {stepsText.map((step, i) => (
                            <p key={i}>{step}</p>
                          ))}
                        </div>
                      </div>
                    )}

                    <br /><br />
                    <div className="button cust-btn-2" onClick={() => setShowMetrics((prev) => !prev)}>
                      {showMetrics ? "Hide Evaluation Metrics" : "See Evaluation Metrics"}
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Evaluation Metrics */}
      {showMetrics && apiResult?.metrics?.length > 0 && (
        <div className="row mt-4">
          <p>
            <ul style={{ listStyle: "none", textAlign: "left" }}>
              <li><strong>Notes:</strong></li>
              <li><em>Accuracy</em> measures how often the model is correct.</li>
              <li><em>Precision</em> shows how many of the predicted positives are actually correct.</li>
              <li><em>Recall</em> indicates how many true positives the model captures.</li>
              <li><em>F1</em> balances precision and recall.</li>
              <li><em>ROC-AUC</em> and <em>PR-AUC</em> measure how well the model distinguishes between classes.</li>
            </ul>
          </p>
          {apiResult.metrics.map((m) => (
            <div key={m.label} className="col-sm-2">
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col mt-0">
                      <h5 className="card-title">{m.label}</h5>
                    </div>
                  </div>
                  <h1 className="mt-1 mb-3">{m.value}%</h1>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ask Isha */}
      <IshaChat cancerName={cancerName} />
    </>
  );
}
